import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit2, Plus, RefreshCw, Save, Search, X } from 'lucide-react';
import apiService, { AdminCreditActionsQuery } from '../../services/api';
import TablePagination from '../company/JobManagement/Components/TablePagination';
import {
  buildPlanCreditCostRows,
  CreditActionType,
  deriveTierOptions,
  formatActionStatus,
  formatCreditCost,
  normalizeActionCodeOptionsResponse,
  normalizeCreditActionsResponse,
  normalizeTierCreditCostsResponse,
  TierCreditCost,
  TierOption,
} from './creditActionUtils';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface CreditActionsManagementProps {
  tiers: TierOption[];
  onNotify?: (message: string, type: ToastType) => void;
}

interface CreditActionFormData {
  action_code: string;
  action_name: string;
  description: string;
  category: string;
  default_credit_cost: string;
}

const initialActionForm: CreditActionFormData = {
  action_code: '',
  action_name: '',
  description: '',
  category: '',
  default_credit_cost: '',
};

const DEFAULT_ROWS_PER_PAGE = 5;
const MAX_API_SEARCH_RESULTS = 100;

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const buildCreditActionQuery = (
  searchTerm: string,
  offset = 0,
  limit = MAX_API_SEARCH_RESULTS
): AdminCreditActionsQuery => {
  const params: AdminCreditActionsQuery = {
    offset,
    limit,
  };

  if (searchTerm) {
    params.search = searchTerm;
  }

  return params;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getTierMetadataTotalCount = (
  response: unknown,
  tierNames: string[],
  fallback: number
): number => {
  if (!isRecord(response) || !isRecord(response.metadata)) {
    return fallback;
  }

  for (const tierName of tierNames) {
    if (!tierName) {
      continue;
    }

    const metadata = response.metadata[tierName];
    if (!isRecord(metadata)) {
      continue;
    }

    const totalCount = Number(metadata.total_count);
    if (Number.isFinite(totalCount) && totalCount >= 0) {
      return totalCount;
    }
  }

  return fallback;
};

const getStatusClasses = (status: string) => {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'active' || normalizedStatus === 'enabled') {
    return 'bg-green-100 text-green-700';
  }

  if (normalizedStatus === 'inactive' || normalizedStatus === 'disabled') {
    return 'bg-gray-100 text-gray-700';
  }

  return 'bg-blue-100 text-blue-700';
};

const parseCreditCost = (value: string, label: string): number | null => {
  if (value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
    throw new Error(`${label} must be a non-negative whole number.`);
  }

  return parsed;
};

const CreditActionsManagement: React.FC<CreditActionsManagementProps> = ({
  tiers,
  onNotify,
}) => {
  const [creditActions, setCreditActions] = useState<CreditActionType[]>([]);
  const [actionTypeRows, setActionTypeRows] = useState<CreditActionType[]>([]);
  const [actionTypesTotalCount, setActionTypesTotalCount] = useState(0);
  const [tierCosts, setTierCosts] = useState<TierCreditCost[]>([]);
  const [planCostPageRows, setPlanCostPageRows] = useState<TierCreditCost[]>([]);
  const [planCostsTotalCount, setPlanCostsTotalCount] = useState(0);
  const [actionCodeOptions, setActionCodeOptions] = useState<string[]>([]);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [loading, setLoading] = useState(true);
  const [costLoading, setCostLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionModalMode, setActionModalMode] = useState<'add' | 'edit' | null>(null);
  const [actionForm, setActionForm] = useState<CreditActionFormData>(initialActionForm);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionModalError, setActionModalError] = useState<string | null>(null);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [costSubmitting, setCostSubmitting] = useState(false);
  const [costModalError, setCostModalError] = useState<string | null>(null);
  const [costForm, setCostForm] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [actionTypesPage, setActionTypesPage] = useState(1);
  const [actionTypesRowsPerPage, setActionTypesRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE
  );
  const [planCostsPage, setPlanCostsPage] = useState(1);
  const [planCostsRowsPerPage, setPlanCostsRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE
  );

  const notify = useCallback(
    (message: string, type: ToastType = 'success') => {
      if (onNotify) {
        onNotify(message, type);
      }
    },
    [onNotify]
  );

  const tierOptions = useMemo(
    () => deriveTierOptions(tiers, tierCosts),
    [tiers, tierCosts]
  );

  const selectedTier = useMemo(
    () => tierOptions.find((tier) => tier.id === selectedTierId) || null,
    [selectedTierId, tierOptions]
  );

  const planCostRows = useMemo(
    () => buildPlanCreditCostRows(creditActions, tierCosts, selectedTierId),
    [creditActions, tierCosts, selectedTierId]
  );

  const normalizedSearchTerm = searchTerm.trim();

  const loadSelectedTierCosts = useCallback(
    async (tierId: string) => {
      if (!tierId) {
        return;
      }

      setCostLoading(true);
      try {
        const tierName = tiers.find((tier) => tier.id === tierId)?.name || tierId;
        const response = await apiService.getAdminCreditActionsByTier(
          tierId,
          buildCreditActionQuery(
            appliedSearchTerm,
            (planCostsPage - 1) * planCostsRowsPerPage,
            planCostsRowsPerPage
          )
        );
        const normalizedRows = normalizeTierCreditCostsResponse(response);
        const rowsForSelection = normalizedRows
          .filter((row) => row.tier_id === tierId)
          .map((row) => ({
            ...row,
            tier_id: row.tier_id || tierId,
            tier_name: row.tier_name || tierName,
          }));

        setPlanCostPageRows(rowsForSelection);
        setPlanCostsTotalCount(
          getTierMetadataTotalCount(
            response,
            [tierName, rowsForSelection[0]?.tier_name || ''],
            rowsForSelection.length
          )
        );
      } catch (error) {
        setError(getErrorMessage(error, 'Failed to load plan credit costs'));
        setPlanCostPageRows([]);
        setPlanCostsTotalCount(0);
      } finally {
        setCostLoading(false);
      }
    },
    [appliedSearchTerm, planCostsPage, planCostsRowsPerPage, tiers]
  );

  const loadCreditActionData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const query = buildCreditActionQuery(appliedSearchTerm);
      const paginatedQuery = buildCreditActionQuery(
        appliedSearchTerm,
        (actionTypesPage - 1) * actionTypesRowsPerPage,
        actionTypesRowsPerPage
      );
      const [actionsResponse, allActionsResponse, actionCodesResponse, tierCostsResponse] =
        await Promise.all([
          apiService.get('/admin/billing/credit-actions', paginatedQuery),
          apiService.getAdminCreditActions(query),
          apiService.getAdminCreditActionCodes().catch(() => []),
          apiService.getAdminCreditActionsByTier(undefined, query).catch(() => []),
        ]);

      const normalizedActionRows = normalizeCreditActionsResponse(actionsResponse.data);
      const totalCount = Number(actionsResponse.headers['x-total-count'] ?? 0);

      setActionTypeRows(normalizedActionRows);
      setActionTypesTotalCount(
        Number.isFinite(totalCount) && totalCount > 0
          ? totalCount
          : normalizedActionRows.length
      );
      setCreditActions(normalizeCreditActionsResponse(allActionsResponse));
      setActionCodeOptions(normalizeActionCodeOptionsResponse(actionCodesResponse));
      setTierCosts(normalizeTierCreditCostsResponse(tierCostsResponse));
    } catch (error) {
      setError(getErrorMessage(error, 'Failed to load credit action data'));
      setActionTypeRows([]);
      setActionTypesTotalCount(0);
      setCreditActions([]);
      setPlanCostPageRows([]);
      setPlanCostsTotalCount(0);
      setTierCosts([]);
    } finally {
      setLoading(false);
    }
  }, [actionTypesPage, actionTypesRowsPerPage, appliedSearchTerm]);

  useEffect(() => {
    loadCreditActionData();
  }, [loadCreditActionData]);

  useEffect(() => {
    if (!selectedTierId && tierOptions.length > 0) {
      setSelectedTierId(tierOptions[0].id);
    }
  }, [selectedTierId, tierOptions]);

  useEffect(() => {
    if (selectedTierId) {
      loadSelectedTierCosts(selectedTierId);
    }
  }, [loadSelectedTierCosts, selectedTierId]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(actionTypesTotalCount / actionTypesRowsPerPage)
    );

    if (actionTypesPage > totalPages) {
      setActionTypesPage(totalPages);
    }
  }, [actionTypesPage, actionTypesRowsPerPage, actionTypesTotalCount]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(planCostsTotalCount / planCostsRowsPerPage)
    );

    if (planCostsPage > totalPages) {
      setPlanCostsPage(totalPages);
    }
  }, [planCostsPage, planCostsRowsPerPage, planCostsTotalCount]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setActionTypesPage(1);
    setPlanCostsPage(1);

    if (appliedSearchTerm !== normalizedSearchTerm) {
      setAppliedSearchTerm(normalizedSearchTerm);
      return;
    }

    if (actionTypesPage === 1) {
      loadCreditActionData();
    }

    if (planCostsPage === 1 && selectedTierId) {
      loadSelectedTierCosts(selectedTierId);
    }
  };

  const handleRefresh = () => {
    loadCreditActionData();
    if (selectedTierId) {
      loadSelectedTierCosts(selectedTierId);
    }
  };

  const openAddActionModal = () => {
    setActionModalMode('add');
    setActionForm({
      ...initialActionForm,
      action_code: actionCodeOptions[0] || '',
    });
    setActionModalError(null);
  };

  const openEditActionModal = (action: CreditActionType) => {
    setActionModalMode('edit');
    setActionForm({
      action_code: action.action_code,
      action_name: action.action_name,
      description: action.description || '',
      category: action.category || '',
      default_credit_cost:
        action.default_credit_cost === null ? '' : String(action.default_credit_cost),
    });
    setActionModalError(null);
  };

  const closeActionModal = () => {
    if (actionSubmitting) {
      return;
    }

    setActionModalMode(null);
    setActionForm(initialActionForm);
    setActionModalError(null);
  };

  const handleActionFormChange = (
    field: keyof CreditActionFormData,
    value: string
  ) => {
    setActionForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleActionFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setActionSubmitting(true);
    setActionModalError(null);

    try {
      const actionCode = actionForm.action_code.trim();
      const actionName = actionForm.action_name.trim();
      const defaultCreditCost = parseCreditCost(
        actionForm.default_credit_cost,
        'Default credit cost'
      );

      if (!actionCode) {
        throw new Error('Action code is required.');
      }

      if (!actionName) {
        throw new Error('Action name is required.');
      }

      if (defaultCreditCost === null) {
        throw new Error('Default credit cost is required.');
      }

      const payload = {
        action_code: actionCode,
        action_name: actionName,
        description: actionForm.description.trim() || null,
        category: actionForm.category.trim() || null,
        default_credit_cost: defaultCreditCost,
      };

      if (actionModalMode === 'add') {
        await apiService.createAdminCreditActionType(payload);
        notify('Credit action type added successfully.');
      } else {
        await apiService.updateAdminCreditActionType(payload);
        notify('Credit action type updated successfully.');
      }

      closeActionModal();
      await loadCreditActionData();
      if (selectedTierId) {
        await loadSelectedTierCosts(selectedTierId);
      }
    } catch (error) {
      setActionModalError(
        getErrorMessage(error, 'Failed to save credit action type')
      );
    } finally {
      setActionSubmitting(false);
    }
  };

  const openCostModal = () => {
    if (!selectedTierId) {
      setCostModalError('Choose a tier before editing plan credit costs.');
      return;
    }

    const nextCostForm = planCostRows.reduce<Record<string, string>>((form, row) => {
      form[row.action_code] =
        row.credit_cost === null || row.credit_cost === undefined
          ? ''
          : String(row.credit_cost);
      return form;
    }, {});

    setCostForm(nextCostForm);
    setCostModalError(null);
    setCostModalOpen(true);
  };

  const closeCostModal = () => {
    if (costSubmitting) {
      return;
    }

    setCostModalOpen(false);
    setCostForm({});
    setCostModalError(null);
  };

  const handleCostFormChange = (actionCode: string, value: string) => {
    setCostForm((current) => ({
      ...current,
      [actionCode]: value,
    }));
  };

  const handleCostFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedTierId) {
      return;
    }

    setCostSubmitting(true);
    setCostModalError(null);

    try {
      const actionCosts = planCostRows.reduce<Record<string, number>>(
        (payload, row) => {
          const value = costForm[row.action_code] || '';
          const parsed = parseCreditCost(value, row.action_code);

          if (parsed !== null) {
            payload[row.action_code] = parsed;
          }

          return payload;
        },
        {}
      );

      if (Object.keys(actionCosts).length === 0) {
        throw new Error('Enter at least one credit cost.');
      }

      await apiService.updateAdminCreditActionCosts(selectedTierId, actionCosts);
      setTierCosts((current) =>
        current.map((row) => {
          if (
            row.tier_id === selectedTierId &&
            Object.prototype.hasOwnProperty.call(actionCosts, row.action_code)
          ) {
            return {
              ...row,
              credit_cost: actionCosts[row.action_code],
            };
          }

          return row;
        })
      );
      await loadSelectedTierCosts(selectedTierId);
      notify('Plan credit costs updated successfully.');
      closeCostModal();
    } catch (error) {
      setCostModalError(
        getErrorMessage(error, 'Failed to update plan credit costs')
      );
    } finally {
      setCostSubmitting(false);
    }
  };

  const statusBadge = (action: CreditActionType) => {
    const status = formatActionStatus(action);

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusClasses(status)}`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Credit Actions</h2>
          {appliedSearchTerm && (
            <p className="mt-1 text-sm text-gray-500">
              {actionTypesTotalCount} action types and{' '}
              {planCostsTotalCount} plan cost rows matching
            </p>
          )}
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
          <form
            className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
            onSubmit={handleSearchSubmit}
          >
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search action types and plan costs"
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <Search className="h-4 w-4" />
              Search
            </button>

            {searchTerm && (
              <button
                type="button"
                onClick={clearSearch}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </form>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Action Types</h3>
          <button
            type="button"
            onClick={openAddActionModal}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
            title="Add credit action type"
          >
            <Plus className="h-4 w-4" />
            Add Action Type
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Action
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Action Code
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Description
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Category
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Default Credit Cost
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {actionTypeRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-gray-500">
                    {appliedSearchTerm
                      ? 'No credit action types match the current search.'
                      : 'No credit action types found.'}
                  </td>
                </tr>
              ) : (
                actionTypeRows.map((action) => (
                  <tr key={action.action_code} className="hover:bg-gray-50">
                    <td className="px-5 py-4 text-sm font-medium text-gray-900">
                      {action.action_name || '-'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                        {action.action_code || '-'}
                      </span>
                    </td>
                    <td className="max-w-xs px-5 py-4 text-sm text-gray-600">
                      <span className="line-clamp-2">{action.description || '-'}</span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      {action.category || '-'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900">
                      {formatCreditCost(action.default_credit_cost)}
                    </td>
                    <td className="px-5 py-4 text-sm">{statusBadge(action)}</td>
                    <td className="px-5 py-4 text-sm">
                      <button
                        type="button"
                        onClick={() => openEditActionModal(action)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {actionTypesTotalCount > actionTypesRowsPerPage && (
          <TablePagination
            currentPage={actionTypesPage}
            totalCount={actionTypesTotalCount}
            rowsPerPage={actionTypesRowsPerPage}
            onPageChange={(page) => setActionTypesPage(page)}
            onRowsPerPageChange={(rows) => {
              setActionTypesRowsPerPage(rows);
              setActionTypesPage(1);
            }}
          />
        )}
      </section>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-900">Plan Credit Costs</h3>
            <select
              value={selectedTierId}
              onChange={(event) => {
                setSelectedTierId(event.target.value);
                setPlanCostsPage(1);
              }}
              className="min-w-[220px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {tierOptions.length === 0 ? (
                <option value="">No tiers available</option>
              ) : (
                tierOptions.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))
              )}
            </select>
            {costLoading && (
              <span className="text-xs font-medium text-gray-500">Loading costs...</span>
            )}
          </div>
          <button
            type="button"
            onClick={openCostModal}
            disabled={!selectedTierId || creditActions.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Edit2 className="h-4 w-4" />
            {planCostRows.some((row) => row.credit_cost !== null) ? 'Edit Costs' : 'Add Costs'}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Action Code
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Action
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Credit Cost
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {planCostPageRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-5 py-8 text-center text-sm text-gray-500">
                    {appliedSearchTerm
                      ? 'No plan credit costs match the current search.'
                      : 'No credit actions available.'}
                  </td>
                </tr>
              ) : (
                planCostPageRows.map((row) => (
                  <tr key={row.action_code} className="hover:bg-gray-50">
                    <td className="px-5 py-4 text-sm text-gray-700">
                      <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                        {row.action_code}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-900">
                      {row.action_name}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900">
                      {formatCreditCost(row.credit_cost)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {planCostsTotalCount > planCostsRowsPerPage && (
          <TablePagination
            currentPage={planCostsPage}
            totalCount={planCostsTotalCount}
            rowsPerPage={planCostsRowsPerPage}
            onPageChange={(page) => setPlanCostsPage(page)}
            onRowsPerPageChange={(rows) => {
              setPlanCostsRowsPerPage(rows);
              setPlanCostsPage(1);
            }}
          />
        )}
      </section>

      {actionModalMode && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 px-4 py-8">
          <div className="mx-auto max-w-2xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {actionModalMode === 'add'
                    ? 'Add Credit Action Type'
                    : 'Edit Credit Action Type'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeActionModal}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close credit action type dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="space-y-5 px-6 py-6" onSubmit={handleActionFormSubmit}>
              {actionModalError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {actionModalError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Action Code *
                  </label>
                  {actionModalMode === 'add' ? (
                    <select
                      required
                      value={actionForm.action_code}
                      onChange={(event) =>
                        handleActionFormChange('action_code', event.target.value)
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="" disabled>
                        Select action code
                      </option>
                      {actionCodeOptions.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={actionForm.action_code}
                      readOnly
                      className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-700"
                    />
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Action Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={actionForm.action_name}
                    onChange={(event) =>
                      handleActionFormChange('action_name', event.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <input
                    type="text"
                    value={actionForm.category}
                    onChange={(event) =>
                      handleActionFormChange('category', event.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Default Credit Cost *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    required
                    value={actionForm.default_credit_cost}
                    onChange={(event) =>
                      handleActionFormChange(
                        'default_credit_cost',
                        event.target.value
                      )
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={actionForm.description}
                    onChange={(event) =>
                      handleActionFormChange('description', event.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={closeActionModal}
                  disabled={actionSubmitting}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionSubmitting}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                >
                  <Save className="h-4 w-4" />
                  {actionSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {costModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 px-4 py-8">
          <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedTier?.name || 'Plan'} Credit Costs
                </h3>
              </div>
              <button
                type="button"
                onClick={closeCostModal}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close plan credit costs dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCostFormSubmit}>
              <div className="max-h-[68vh] overflow-y-auto px-6 py-6">
                {costModalError && (
                  <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {costModalError}
                  </div>
                )}

                <div className="overflow-x-auto rounded-md border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Action Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Action
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Credits
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {planCostRows.map((row) => (
                        <tr key={row.action_code}>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                              {row.action_code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {row.action_name}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={costForm[row.action_code] || ''}
                              onChange={(event) =>
                                handleCostFormChange(
                                  row.action_code,
                                  event.target.value
                                )
                              }
                              className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={closeCostModal}
                  disabled={costSubmitting}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={costSubmitting}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                >
                  <Save className="h-4 w-4" />
                  {costSubmitting ? 'Saving...' : 'Save Costs'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditActionsManagement;
